import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Play } from 'lucide-react';
import { getAssetUrl } from '../../Api';
import { Cabecalho, Foto, preencher } from './comum';
import { anosDe, emissoesPorAno, somaRemocoes, toneladas } from './gee';
import * as S from './style';

const TONS_DAS_COLUNAS = ['var(--brand-500)', 'var(--brand-900)', 'var(--brand-600)', 'var(--brand-800)'];

// Miniatura do próprio YouTube quando o Squidex não tem capa. Com `light={true}`
// o react-player buscava a miniatura num serviço de terceiros (noembed), que
// falha à toa e deixava a capa preta.
const miniaturaDoYoutube = (url = '') => {
  const id = url.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([\w-]{11})/)?.[1];
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// O vídeo com capa por cima. Três estados:
//   - antes do primeiro clique: só a capa; o player do YouTube nem carrega;
//   - assistindo: a capa e a legenda somem, o player aparece;
//   - clicou fora do vídeo ou trocou de aba: o vídeo pausa e a capa volta,
//     como se não tivesse clicado. O player continua montado por baixo, então
//     o próximo clique retoma de onde parou, sem recarregar.
const Video = ({ secao }) => {
  const capa = secao.videoCapa?.[0];
  const video = secao.enderecoDoVideo;
  const imagemDaCapa = capa ? getAssetUrl(capa, { width: 1280, format: 'webp' }) : miniaturaDoYoutube(video);

  const [carregado, setCarregado] = useState(false);
  const [assistindo, setAssistindo] = useState(false);
  const caixa = useRef(null);
  const player = useRef(null);

  useEffect(() => {
    if (!assistindo) return undefined;
    const voltarParaCapa = () => setAssistindo(false);
    const aoClicar = (e) => {
      if (caixa.current && !caixa.current.contains(e.target)) voltarParaCapa();
    };
    // Clique dentro do player (iframe do YouTube) não chega ao documento, então
    // pausar, avançar e tela cheia nos controles do YouTube não trazem a capa.
    const aoTrocarDeAba = () => {
      if (document.hidden) voltarParaCapa();
    };
    document.addEventListener('pointerdown', aoClicar);
    document.addEventListener('visibilitychange', aoTrocarDeAba);
    return () => {
      document.removeEventListener('pointerdown', aoClicar);
      document.removeEventListener('visibilitychange', aoTrocarDeAba);
    };
  }, [assistindo]);

  return (
    <S.Midia ref={caixa}>
      {carregado && (
        <ReactPlayer
          ref={player}
          className="react-player"
          url={video}
          playing={assistindo}
          controls
          width="100%"
          height="100%"
          // Terminou: volta a capa, e o próximo play começa do início.
          onEnded={() => {
            setAssistindo(false);
            player.current?.seekTo(0, 'seconds');
          }}
        />
      )}
      {!assistindo && (
        <S.CapaDoVideo
          type="button"
          $imagem={imagemDaCapa}
          onClick={() => {
            setCarregado(true);
            setAssistindo(true);
          }}
        >
          <S.Play>
            <Play size={30} fill="currentColor" aria-hidden="true" />
            {secao.videoBotao && <S.SoParaLeitor>{secao.videoBotao}</S.SoParaLeitor>}
          </S.Play>
        </S.CapaDoVideo>
      )}
      {!assistindo && secao.videoLegenda && <figcaption>{secao.videoLegenda}</figcaption>}
    </S.Midia>
  );
};

const Midia = ({ secao }) => {
  const capa = secao.videoCapa?.[0];
  if (secao.enderecoDoVideo) return <Video secao={secao} />;
  if (!capa) return null;

  // Só capa, ainda sem link do vídeo.
  return (
    <S.Midia>
      <Foto id={capa} largura={1280} alt={secao.videoLegenda || ''} />
      <span className="sombra" aria-hidden="true" />
      {secao.videoLegenda && <figcaption>{secao.videoLegenda}</figcaption>}
    </S.Midia>
  );
};

// Linha de cabeçalho dos quadros: coluna do rótulo e coluna do valor.
const Colunas = ({ esquerda, direita }) =>
  esquerda || direita ? (
    <div>
      <span>{esquerda}</span>
      <span>{direita}</span>
    </div>
  ) : null;

// O painel de remoções: a visão geral da cooperativa, com todas as unidades
// somadas. `remocoes` e `emissoes` são as linhas por ano e unidade. Todo texto
// vem de `secao` (secao-eucalipto); campo vazio some.
const Painel = ({ secao, remocoes, emissoes }) => {
  const anos = anosDe(remocoes);
  const remDoAno = (a) => somaRemocoes(remocoes.filter((l) => l.ano === a));
  const maxAno = Math.max(...anos.map(remDoAno), 1);
  const emi = emissoesPorAno(emissoes);

  const comUnidade = (v) => [toneladas(v), secao.unidade].filter(Boolean).join(' ');
  // Balanço só existe em ano com emissão e remoção registradas.
  const balancos = emi.filter((e) => anos.includes(e.ano));

  const leitura = [
    ...anos.map((a) => ({ titulo: preencher(secao.leituraRemocoesTitulo, { ano: a }), valor: remDoAno(a) })),
    ...balancos.map((e) => ({
      titulo: preencher(secao.leituraBalancoTitulo, { ano: e.ano }),
      valor: e.total - remDoAno(e.ano),
    })),
  ];

  return (
    <>
      <S.Painel>
        <S.PainelGrade>
          <S.Quadro>
            {secao.evolucaoTitulo && <p>{secao.evolucaoTitulo}</p>}
            <S.Colunas>
              {anos.map((a, k) => (
                <div key={a}>
                  <p>{comUnidade(remDoAno(a))}</p>
                  <div>
                    <span
                      style={{
                        height: `${Math.max(6, Math.round((remDoAno(a) / maxAno) * 100))}%`,
                        background: TONS_DAS_COLUNAS[k % TONS_DAS_COLUNAS.length],
                      }}
                    />
                  </div>
                  <strong>{a}</strong>
                </div>
              ))}
            </S.Colunas>
          </S.Quadro>

          {/* Lado a lado: evolução, emissões e balanço. */}
          {emi.length > 0 && (
            <>
              <S.Quadro>
                {secao.emissoesTitulo && <p>{secao.emissoesTitulo}</p>}
                <S.Linhas>
                  <Colunas esquerda={secao.colunaAno} direita={secao.emissoesColuna} />
                  {emi.map((e) => (
                    <div key={e.ano}>
                      <span>{e.ano}</span>
                      <strong>
                        {toneladas(e.total)} {secao.unidade && <small>{secao.unidade}</small>}
                      </strong>
                    </div>
                  ))}
                </S.Linhas>
              </S.Quadro>
              <S.Quadro>
                {secao.balancoTitulo && <p>{secao.balancoTitulo}</p>}
                <S.Linhas>
                  <Colunas esquerda={secao.colunaAno} direita={secao.balancoColuna} />
                  {/* Ano sem remoção registrada não tem balanço: mostrar a emissão
                      inteira como "saldo" diria que a remoção foi zero. */}
                  {emi.map((e) => (
                    <div key={e.ano}>
                      <span>{e.ano}</span>
                      {anos.includes(e.ano) ? (
                        <strong>
                          {toneladas(e.total - remDoAno(e.ano))} {secao.unidade && <small>{secao.unidade}</small>}
                        </strong>
                      ) : (
                        <strong>–</strong>
                      )}
                    </div>
                  ))}
                </S.Linhas>
              </S.Quadro>
            </>
          )}
        </S.PainelGrade>
      </S.Painel>

      {leitura.length > 0 && (
        <S.Bloco style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', marginTop: 'var(--sp-8)' }}>
          {secao.leituraTitulo && <S.H3>{secao.leituraTitulo}</S.H3>}
          <S.Leitura>
            {leitura.map((d, i) => (
              <article key={i}>
                <span>{String(i + 1).padStart(2, '0')}</span>
                {d.titulo && <p style={{ fontWeight: 700, lineHeight: 1.3, color: 'var(--text-strong)' }}>{d.titulo}</p>}
                <strong>{toneladas(d.valor)}</strong>
                {secao.unidade && <small>{secao.unidade}</small>}
              </article>
            ))}
          </S.Leitura>
        </S.Bloco>
      )}
    </>
  );
};

export const Eucalipto = ({ secao, remocoes, emissoes }) => {
  const temMidia = Boolean(secao.enderecoDoVideo || secao.videoCapa?.[0]);
  const faixa = (secao.faixa || []).filter((c) => c.titulo || c.descricao);
  const processo = (secao.processo || []).filter((c) => c.titulo || c.descricao);
  // Sem remoções sincronizadas, o painel não tem o que mostrar -- e o texto
  // acima dele e a metodologia falam do painel, então somem juntos.
  const temPainel = remocoes.length > 0;

  return (
    <S.Secao id="eucalipto" $wash>
      <S.Container>
        <S.EucTopo $comMidia={temMidia}>
          <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} markdown />
          <Midia secao={secao} />
        </S.EucTopo>

        {faixa.length > 0 && (
          <S.EucFaixa>
            {faixa.map((c, i) => (
              <div key={i}>
                {c.titulo && <strong>{c.titulo}</strong>}
                {c.descricao && <S.Nota>{c.descricao}</S.Nota>}
              </div>
            ))}
          </S.EucFaixa>
        )}

        {temPainel && (
          <S.Bloco style={{ marginTop: 'var(--sp-8)' }}>
            {(secao.painelTitulo || secao.painelTexto) && (
              <S.Cabecalho style={{ marginBottom: 0 }}>
                {secao.painelTitulo && <S.H3>{secao.painelTitulo}</S.H3>}
                {secao.painelTexto && <S.Paragrafo>{secao.painelTexto}</S.Paragrafo>}
              </S.Cabecalho>
            )}
            <Painel secao={secao} remocoes={remocoes} emissoes={emissoes} />
          </S.Bloco>
        )}

        {processo.length > 0 && (
          <S.Bloco style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', marginTop: 'var(--sp-8)' }}>
            {secao.processoTitulo && <S.H3 style={{ maxWidth: '68ch' }}>{secao.processoTitulo}</S.H3>}
            <S.Etapas>
              {processo.map((c, i) => (
                <div key={i}>
                  <span>{String(i + 1).padStart(2, '0')}</span>
                  {c.titulo && <strong>{c.titulo}</strong>}
                  {c.descricao && <S.Nota>{c.descricao}</S.Nota>}
                </div>
              ))}
            </S.Etapas>
          </S.Bloco>
        )}

        {temPainel && secao.metodologia && (
          <S.Metodologia>
            {secao.metodologiaChapeu && <S.Rotulo>{secao.metodologiaChapeu}</S.Rotulo>}
            <S.Paragrafo>{secao.metodologia}</S.Paragrafo>
          </S.Metodologia>
        )}
      </S.Container>
    </S.Secao>
  );
};
